import { useEffect, useState } from 'react';
import { GenericAbortSignal } from 'axios';

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';

import Group, { GroupOptions } from 'models/Group';
import { PaginatedItems } from 'models/Paginated';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const groupsQueryKeys = {
  index: ['groups'] as const,
};

export enum GroupAccessMode {
  Allowed = 'allowed',
  Network = 'network',
  ManagerNetwork = 'manager_network',
}

export type IndexParams = {
  name?: string;
  type?: string;
  'type.not'?: string;
  locationType?: string;
  networkClientId?: string;
  groupType?: string | string[];
  isManager?: boolean;
  isProvider?: boolean;
  viewOnly?: boolean;
  clientId?: string;
  clientIdOrNetworkClientId?: string;
  // false relaxes the scoped-client lens for pages that address a client
  // explicitly (the boundary — group membership — still applies).
  enforceScopedClient?: boolean;
  'id.not'?: string;
  accessModes?: GroupAccessMode[];
  // The picking group: its location's preferred providers come first, with preferred set.
  preferredForGroupId?: string;
  // Referral pickers: a client that uses central intake is offered as the client, so its groups are left out.
  forReferral?: boolean;
  // The discharge picker: the stay being discharged, so the groups its discharge lock refuses are left out.
  dischargeLocationEpisodeId?: string;
} & BaseIndexQueryParams;
export async function indexGroups(params: IndexParams, signal: GenericAbortSignal) {
  return http.get<PaginatedItems<GroupOptions>>(`groups`, { params, signal }).then((res) => {
    return {
      ...res.data,
      data: res.data.items.map((x) => new Group(x)),

      // deprecated - use `data` instead
      items: res.data.items.map((x) => new Group(x)),
    };
  });
}

// One GET /groups picker query. search undefined is the unsearched list.
export const infiniteGroupsQuery = (params: IndexParams, search?: string) =>
  infiniteQueryOptions({
    queryKey: [groupsQueryKeys.index, { ...params, search }],
    queryFn: ({ signal, pageParam }) => indexGroups({ pageSize: 10, ...params, search, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
  });

// Pickers load on menu open. enabled loads the first page as soon as the params are set instead, for a
// picker that needs its results before then (the preferred provider legend).
export const useInfiniteGroups = (params: IndexParams, { enabled = false } = {}) => {
  const [search, setSearch] = useState<string>();

  const options = useInfiniteQuery({ ...infiniteGroupsQuery(params, search), enabled });

  useEffect(() => {
    // An enabled query fetches the new search's key by itself.
    if (search !== undefined && !enabled) options.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return { options, onDebouncedSearch: setSearch };
};
